import { apiFetch } from "@/integrations/backend/client";
import { DIFFICULTY, Difficulty, MOVEMENT_PATTERNS, MovementPattern, MUSCLE_ACTIONS, MuscleAction, MUSCLE_GROUPS, MuscleGroup } from "./enums";
import type { ExerciseTypeRoles } from "@/lib/exercise-type-roles";
import { fetchEquipmentOptions, type EquipmentOption } from "./equipment";

// Re-export for convenience
export type { EquipmentOption };

export type AdminExerciseVM = {
  id: string;
  name: string;
  categories: MovementPattern[];
  primary_muscles: MuscleGroup[];
  secondary_muscles: MuscleGroup[];
  equipment: string[]; // equipment names used by UI filters and chips
  difficulties: Difficulty[];
  instructions: string;
  is_compound: boolean;
  is_deleted: boolean;
  muscle_actions: MuscleAction[];
  video_url?: string | null;
  image_url?: string | null;
  exercise_types?: string[];
  exercise_type_roles?: ExerciseTypeRoles;
  programming_use?: string | null;
  tips?: string;
  ai_notes?: string | null;
  ai_generated?: boolean;
};

export type ClientExerciseVM = Omit<AdminExerciseVM, "is_deleted"> & {
  tips?: string;
  programming_use?: string | null;
  exercise_type_roles?: ExerciseTypeRoles;
};

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

export async function loadExercisesWithRelations(): Promise<AdminExerciseVM[]> {
  const res = await apiFetch("/api/v1/exercises/admin-list", { method: "GET" });
  if (!res.ok) throw new Error(`exercises_admin_list_failed:${res.status}`);
  return (await res.json()) as AdminExerciseVM[];
}

export async function listExercises(): Promise<ClientExerciseVM[]> {
  const res = await apiFetch("/api/v1/exercises", { method: "GET" });
  if (!res.ok) throw new Error(`exercises_list_failed:${res.status}`);
  const json = (await res.json()) as unknown;
  if (!Array.isArray(json)) return [];

  // Defensive normalization: backend sometimes returns `null`/missing arrays for some exercises.
  return (json as Array<Record<string, unknown>>).map((ex) => {
    return {
      ...(ex as unknown as ClientExerciseVM),
      categories: asArray(ex.categories),
      primary_muscles: asArray(ex.primary_muscles),
      secondary_muscles: asArray(ex.secondary_muscles),
      muscle_actions: asArray(ex.muscle_actions),
      equipment: asArray(ex.equipment),
      difficulties: asArray(ex.difficulties),
    } satisfies ClientExerciseVM;
  });
}

export type UpsertExerciseInput = {
  id?: string;
  name: string;
  instructions: string;
  tips?: string;
  ai_notes?: string;
  is_compound: boolean;
  categories: MovementPattern[];
  primary_muscles: MuscleGroup[];
  secondary_muscles: MuscleGroup[];
  muscle_actions: MuscleAction[];
  equipmentNames: string[]; // chosen by display names from UI
  difficulties: Difficulty[];
  video_url?: string;
  exercise_types?: string[];
  exercise_type_roles?: ExerciseTypeRoles;
  programming_use?: string;
};

export async function upsertExercise(payload: UpsertExerciseInput, equipmentOptions: EquipmentOption[], imageFile?: File): Promise<string> {
  // Use FormData if image file is provided, otherwise use JSON
  if (imageFile) {
    const form = new FormData();
    if (payload.id) form.append("id", payload.id);
    form.append("name", payload.name);
    form.append("instructions", payload.instructions || "");
    if (payload.tips) form.append("tips", payload.tips);
    form.append("is_compound", String(payload.is_compound));
    form.append("categories", JSON.stringify(payload.categories));
    form.append("primary_muscles", JSON.stringify(payload.primary_muscles));
    form.append("secondary_muscles", JSON.stringify(payload.secondary_muscles));
    form.append("muscle_actions", JSON.stringify(payload.muscle_actions));
    form.append("equipmentNames", JSON.stringify(payload.equipmentNames));
    form.append("difficulties", JSON.stringify(payload.difficulties));
    form.append("exercise_types", JSON.stringify(payload.exercise_types || []));
    if (payload.exercise_type_roles) {
      form.append("exercise_type_roles", JSON.stringify(payload.exercise_type_roles));
    }
    if (payload.programming_use) form.append("programming_use", payload.programming_use);
    if (payload.video_url) form.append("video_url", payload.video_url);
    form.append("ai_notes", payload.ai_notes ?? "");
    form.append("image", imageFile);
    const res = await apiFetch("/api/v1/exercises/upsert", {
      method: "POST",
      body: form,
    });
    if (!res.ok) throw new Error(`exercises_upsert_failed:${res.status}`);
    const json = await res.json();
    return json.id as string;
  } else {
    const res = await apiFetch("/api/v1/exercises/upsert", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`exercises_upsert_failed:${res.status}`);
    const json = await res.json();
    return json.id as string;
  }
}

export async function softDeleteExercise(id: string): Promise<void> {
  const res = await apiFetch(`/api/v1/exercises/${id}/soft-delete`, { method: "POST" });
  if (!res.ok) throw new Error(`exercise_soft_delete_failed:${res.status}`);
}

export async function reactivateExercise(id: string): Promise<void> {
  const res = await apiFetch(`/api/v1/exercises/${id}/reactivate`, { method: "POST" });
  if (!res.ok) throw new Error(`exercise_reactivate_failed:${res.status}`);
}


